import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { PersistenceStats } from './PersistenceStats';
import { apiUrls, buildApiUrl } from '../config/api-config';

interface DiagnosticResult {
  test: string;
  status: 'success' | 'error' | 'pending';
  message: string;
  url?: string;
}

export function ConnectionDiagnostic() {
  const [results, setResults] = useState<DiagnosticResult[]>([]);
  const [running, setRunning] = useState(false);

  const runDiagnostic = async () => {
    setRunning(true);
    setResults([]);

    const tests: Array<{ name: string; url: string; method?: string; body?: any }> = [
      { name: 'Health Check', url: buildApiUrl('/games/storm/health') },
      { name: 'Session Config', url: apiUrls.sessionConfig('test') },
      { name: 'Load Configs', url: apiUrls.loadConfigs() },
      { name: 'Session Responses', url: buildApiUrl('/games/storm/session/test/responses') },
      { 
        name: 'Import Config', 
        url: apiUrls.importConfig('test'), 
        method: 'POST',
        body: { config: { triggers: { en: ['test'] }, categories: { en: ['test'] } } }
      }
    ];

    for (const test of tests) {
      setResults(prev => [...prev, { 
        test: test.name, 
        status: 'pending', 
        message: 'Testing...',
        url: test.url
      }]);

      try {
        const fetchOptions: RequestInit = { 
          method: test.method || 'GET',
          headers: test.method === 'POST' ? { 'Content-Type': 'application/json' } : {}
        };

        // Add body for POST requests
        if (test.method === 'POST') {
          fetchOptions.body = JSON.stringify(test.body || { test: true });
        }

        const response = await fetch(test.url, fetchOptions);

        if (response.ok) {
          const responseText = await response.text();
          let message = `✅ Success (${response.status})`;
          
          // Try to parse JSON response for more details
          try {
            const jsonResponse = JSON.parse(responseText);
            if (jsonResponse.message) {
              message += ` - ${jsonResponse.message}`;
            }
          } catch {
            // Not JSON, use default message
          }

          setResults(prev => prev.map(r => 
            r.test === test.name 
              ? { ...r, status: 'success', message }
              : r
          ));
        } else {
          setResults(prev => prev.map(r => 
            r.test === test.name 
              ? { ...r, status: 'error', message: `❌ HTTP ${response.status}: ${response.statusText}` }
              : r
          ));
        }
      } catch (error) {
        setResults(prev => prev.map(r => 
          r.test === test.name 
            ? { ...r, status: 'error', message: `❌ ${error.message}` }
            : r
        ));
      }
    }

    setRunning(false);
  };

  useEffect(() => {
    runDiagnostic();
  }, []);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">🔍 Connection Diagnostic</h3>
        <Button 
          onClick={runDiagnostic} 
          disabled={running}
          size="sm"
        >
          {running ? 'Testing...' : 'Run Tests'}
        </Button>
      </div>

      <div className="space-y-3">
        {results.map((result, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div className="flex-1">
              <div className="font-medium">{result.test}</div>
              <div className="text-sm text-gray-600">{result.message}</div>
              {result.url && (
                <div className="text-xs text-gray-500 mt-1 break-all">
                  {result.url}
                </div>
              )}
            </div>
            <div className="ml-4">
              {result.status === 'pending' && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              )}
              {result.status === 'success' && (
                <div className="text-green-600">✅</div>
              )}
              {result.status === 'error' && (
                <div className="text-red-600">❌</div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-xs text-gray-500">
        <p><strong>Current hostname:</strong> {window.location.hostname}</p>
        <p><strong>Current protocol:</strong> {window.location.protocol}</p>
        <p><strong>API Base URL:</strong> {apiUrls.sessionConfig('test').split('/session')[0]}</p>
      </div>

      {/* Persistence Stats */}
      <div className="mt-6">
        <PersistenceStats />
      </div>
    </Card>
  );
}
